import * as vp from 'vopay';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'UI';

const { Box, Select, TextField, MultiSelect, DateRangePicker } = vp;

type SearchFormProps = {
  type: string;
  currency: string;
  searchTerm: string;
  timeRange: string;
  startDate: vp.Timestamp | undefined;
  endDate: vp.Timestamp | undefined;
  country: string;
  section: 'Payable' | 'Receivable';
  handleTypeChange: (event: string) => void;
  handleCurrencyChange: (event: string) => void;
  handleSearchTermChange: (event: string) => void;
  handleTimeRangeChange: (event: string) => void;
  handleDateRangeChange: (event: [vp.Timestamp, vp.Timestamp]) => void;
  getTransactionLists: () => void;
};

type FC = FunctionComponent<SearchFormProps>;

const AccountingSystemSearchForm: FC = (props: SearchFormProps) => {
  const {
    type,
    currency,
    searchTerm,
    timeRange,
    startDate,
    endDate,
    handleTypeChange,
    handleCurrencyChange,
    handleSearchTermChange,
    handleTimeRangeChange,
    handleDateRangeChange,
    getTransactionLists,
    section,
  } = props;
  const { t } = useTranslation('translations', { keyPrefix: 'AccountingSystem.SearchForm' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const TimeRanges = [
    { value: 'custom', label: t('TimeRanges.custom') },
    { value: 'today', label: t('TimeRanges.today') },
    { value: 'yesterday', label: t('TimeRanges.yesterday') },
    { value: 'thisWeek', label: t('TimeRanges.thisWeek') },
    { value: 'lastWeek', label: t('TimeRanges.lastWeek') },
    { value: 'thisMonth', label: t('TimeRanges.thisMonth') },
    { value: 'lastMonth', label: t('TimeRanges.lastMonth') },
    { value: 'thisQuarter', label: t('TimeRanges.thisQuarter') },
    { value: 'lastQuarter', label: t('TimeRanges.lastQuarter') },
    { value: 'last6Months', label: t('TimeRanges.last6Months') },
    { value: 'last12Months', label: t('TimeRanges.last12Months') },
  ];

  const paymentMethodOptions = [
    { value: 'EFT', label: t('PaymentMethod.Options.eft') },
    { value: 'ACH', label: t('PaymentMethod.Options.ach') },
    { value: 'Debit Card', label: t('PaymentMethod.Options.debitCard') },
    { value: 'Credit Card', label: t('PaymentMethod.Options.creditCard') },
    { value: 'Interac e-Transfer', label: 'Interac e-Transfer' },
    { value: 'VoPay Instant', label: 'VoPay Instant' },
  ];

  const currencyOptions = [
    {
      value: 'CAD',
      label: 'CAD',
    },
    {
      value: 'USD',
      label: 'USD',
    },
  ];

  return (
    <Box jc="sb" flx="col" gap="md">
      <Box gap="sm" wrap="wrap">
        <Box width="175px">
          <Select
            version="2"
            placeholder={section === 'Receivable' ? t('dateReceivablePlaceholder') : t('datePlaceholder')}
            options={TimeRanges}
            name="date"
            value={timeRange}
            onBlur={() => {}}
            onChange={(e) => {
              handleTimeRangeChange(e.target.value);
            }}
          />
        </Box>
        <DateRangePicker
          onChange={(range) => {
            handleDateRangeChange(range);
          }}
          allowBlank
          value={startDate && endDate ? [startDate, endDate] : null}
          triggerVariant="outlineShadow"
        />

        <Box width="200px">
          <MultiSelect
            name="type"
            value={type.split('|')}
            placeholder={t('PaymentMethod.placeholder')}
            options={paymentMethodOptions}
            onChange={(e) => {
              const value = e.target.value;
              const cleanedPaymentMethods =
                value === ''
                  ? []
                  : value
                      .split(',')
                      .map((item) => item.trim())
                      .filter((item) => item !== '');
              handleTypeChange(cleanedPaymentMethods.join('|'));
            }}
            onBlur={() => {}}
            sort={false}
          />
        </Box>
        <Box width="150px">
          <Select
            version="2"
            placeholder={t('Currency.placeholder')}
            options={currencyOptions}
            name="currency"
            value={currency}
            onBlur={() => {}}
            onChange={(e) => {
              handleCurrencyChange(e.target.value);
            }}
          />
        </Box>

        <TextField
          key="AccountingSoftwareKeyword"
          name="AccountingSoftwareKeyword"
          placeholder={t('KeywordPlaceholder')}
          value={searchTerm}
          onBlur={() => {}}
          onChange={(e) => {
            handleSearchTermChange(e.target.value);
          }}
          variant="outlineShadow"
        />
        <Button
          id="search-accounting-software"
          size="large"
          colorScheme="primary"
          disabled={startDate === undefined || endDate === undefined}
          onClick={() => {
            getTransactionLists();
          }}
        >
          {tGlobal('Buttons.search')}
        </Button>
      </Box>
    </Box>
  );
};

export default AccountingSystemSearchForm;
